// collector/session.js
//
// Whose day is on this phone, and what happens when the login expires
// (collector-app P2-B, locked answers L-D and L-J).
//
// WHOSE: every row the collector layer stores carries the signed-in user's
// id, and the layer hydrates and sends only that user's rows — a second
// account on the same phone never sees or sends the first one's day.
//
// AN EXPIRED LOGIN (L-D). A 401 or 419 PAUSES the queue and never clears it;
// nothing here, in the 401 handler or in logout touches the collector's
// storage. The Pinia auth session IS cleared as before — LaunchPage skips its
// token exchange for a signed-in store, so keeping a dead session would make
// the Telegram re-entry fail — but a collector on /collector/ with a day or a
// queue on the phone is HELD instead of sent to /login/ (a password he may not
// have): a small localStorage marker `{ userId, at }` lets the collector
// guard and the cold-boot pre-empt through while he is signed out, and tells
// the layer whose rows to show. The screens show ONE card «افتح يومك من
// تيليجرام» while `authPaused`; the Telegram re-entry (launch handoff →
// `/launch/?t=` → cookie → profile) signs him back in, and the queue resumes
// when the auth store has that user again. The marker lives 30 days (the
// login's own lifetime) and is dropped at boot when the phone holds nothing.
import { ref } from 'vue';
import { collectorStorage } from '@/collector/db.js';
import { isTma } from '@/platform/index.js';
import { useAuthStore } from '@/stores/authStore';

const HOLD_KEY = 'poolit:collector:hold';
const HOLD_TTL_MS = 30 * 24 * 60 * 60 * 1000;
/** A stuck IndexedDB must not decide for /login/: after this, hold. */
const WORK_CHECK_MS = 3_000;
const COLLECTOR_PATH = /^\/collector(\/|$)/;

/** The queue waits for a login (a 401 or 419 came back). */
export const authPaused = ref(false);

/** Ids are strings on the wire (JSON:API); null when nobody is signed in. */
export function normalizeUserId(id) {
  return id == null || id === '' ? null : String(id);
}

function currentPath() {
  return typeof window === 'undefined' ? '' : window.location.pathname;
}

/** The collector's surface (the slashless form included — its normalizer runs after). */
export function isCollectorPath(path = currentPath()) {
  return COLLECTOR_PATH.test(String(path ?? ''));
}

/** The hold marker, or null (none, unreadable, or older than the login's lifetime). */
export function readHold() {
  try {
    const raw = localStorage.getItem(HOLD_KEY);
    if (!raw) return null;
    const hold = JSON.parse(raw);
    const userId = normalizeUserId(hold?.userId);
    if (userId == null || !(Date.now() - Number(hold.at) < HOLD_TTL_MS)) {
      localStorage.removeItem(HOLD_KEY);
      return null;
    }
    return { userId, at: Number(hold.at) };
  } catch {
    return null;
  }
}

function writeHold(userId) {
  try {
    localStorage.setItem(HOLD_KEY, JSON.stringify({ userId, at: Date.now() }));
  } catch {
    /* storage blocked: the hold lasts as long as this page does */
  }
}

export function clearHold() {
  try {
    localStorage.removeItem(HOLD_KEY);
  } catch {
    /* nothing to clear */
  }
}

/** A signed-out collector held on his day (the guards' allowance). */
export function collectorHoldActive() {
  return readHold() != null;
}

/** The collector whose rows the layer reads and sends: signed in, else held. */
export function collectorUserId() {
  return normalizeUserId(useAuthStore().user?.id) ?? readHold()?.userId ?? null;
}

const memoryWork = new Map();

/**
 * The action layer (and the photo queue, under its own `key`) tell the hold
 * what they hold in memory (a write may not be on disk yet). Registering under
 * the same key again replaces the earlier probe.
 */
export function registerLocalWork(probe, key = 'actions') {
  if (typeof probe === 'function') memoryWork.set(key, probe);
  else memoryWork.delete(key);
}

/**
 * A day, a tap not sent or a photo not uploaded for this user — in memory or
 * on the phone. When the phone cannot answer (a slow or failing IndexedDB
 * after a restart) it answers YES: holding a collector who had nothing costs
 * one card; sending one who had his day to /login/ locks him out of it.
 */
export async function hasLocalWork(userId) {
  for (const probe of memoryWork.values()) {
    try {
      if (probe(userId)) return true;
    } catch {
      /* the store is gone: ask the phone */
    }
  }
  try {
    return await Promise.race([
      collectorStorage()
        .hasWork(userId)
        .catch(() => true),
      new Promise((resolve) => {
        setTimeout(() => resolve(true), WORK_CHECK_MS);
      }),
    ]);
  } catch {
    return true;
  }
}

let holding = null;

/**
 * The 401 handler's collector branch (L-D). Call it SYNCHRONOUSLY, before the
 * auth store is cleared: it reads who is signed in. Idempotent — the several
 * 401s of one expired login (profile refresh, the day read, the badge…) make
 * one hold. Resolves true when the collector stays on his day, false when the
 * handler should go on to /login/ as for anyone else.
 */
export function enterAuthHold({ path = currentPath() } = {}) {
  if (isTma()) return Promise.resolve(false);
  const signedIn = normalizeUserId(useAuthStore().user?.id);
  const existing = readHold();
  if (existing && (signedIn == null || signedIn === existing.userId)) {
    authPaused.value = true;
    return Promise.resolve(true);
  }
  if (holding) return holding;
  if (!isCollectorPath(path) || signedIn == null) return Promise.resolve(false);

  writeHold(signedIn);
  authPaused.value = true;
  holding = hasLocalWork(signedIn)
    .then((has) => {
      if (!has) {
        clearHold();
        authPaused.value = false;
      }
      return has;
    })
    .finally(() => {
      holding = null;
    });
  return holding;
}

/** The action layer saw a 401 or 419 on a send: stop sending until the login is back. */
export function pauseForAuth() {
  authPaused.value = true;
}

/**
 * A user is signed in again (the Telegram re-entry, or a login on another
 * tab). Their queue runs; the marker goes only when it was theirs — a
 * different account on the phone never clears another collector's hold.
 */
export function resumeAfterAuth(userId) {
  const id = normalizeUserId(userId);
  if (id == null) return;
  const hold = readHold();
  if (hold && hold.userId === id) clearHold();
  authPaused.value = false;
}

let rebootstrapping = null;

/**
 * An authenticated collector request succeeded while paused: the session is
 * alive. With a user in the store, resume; without one (the login came back
 * on another tab — the Chrome tab and the installed app share cookies),
 * rebuild the store from the cookie (auth init priority 3) — its setSession
 * then resumes through the auth watcher in App.vue.
 */
export function sessionAlive() {
  const userId = normalizeUserId(useAuthStore().user?.id);
  if (userId != null) {
    resumeAfterAuth(userId);
    return Promise.resolve(true);
  }
  if (!rebootstrapping) {
    rebootstrapping = import('@/composables/useAuth')
      .then(({ useAuthInitialize }) => useAuthInitialize().initialize())
      .catch((error) => console.warn('[collector/session] rebuilding the session failed', error))
      .finally(() => {
        rebootstrapping = null;
      });
  }
  return rebootstrapping;
}

/** Tests only. */
export function resetCollectorSessionForTests() {
  authPaused.value = false;
  holding = null;
  rebootstrapping = null;
  memoryWork.clear();
  clearHold();
}
